import snakeCase from 'lodash/snakeCase';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { HookFormText } from 'components/hookForm';

import { CustomAttributeModalMode } from '../../../customAttributeModal.types';
import { AttributeFormFieldValues } from '../../../customAttributeModalForm.types';

const maxLength = 255;

type IdentifierFormFieldProps = {
  /** The modal mode. */
  mode: CustomAttributeModalMode;
  /** The identifiers already in use by other attributes. */
  conflictingIdentifiers: ReadonlySet<string>;
};

/**
 * Represents the field for providing a human readable identifier when creating
 * a custom attribute.
 */
export const IdentifierFormField = ({
  mode,
  conflictingIdentifiers,
}: IdentifierFormFieldProps): JSX.Element | null => {
  const {
    control,
    formState: { isSubmitSuccessful, isSubmitting },
    setValue,
    trigger,
    // TODO: [TEAMS-13693] remove omit after updating to RHF v8
  } = useFormContext<Omit<AttributeFormFieldValues, 'options'>>();

  const [hasUserInput, setHasUserInput] = useState(false);

  const label = useWatch({ control, name: 'label' });

  // Handles copying over the label field value.
  useEffect(() => {
    if (mode === 'create' && !hasUserInput) {
      setValue('identifier', snakeCase(label), {
        shouldTouch: true,
        shouldDirty: true,
      });
    }
  }, [hasUserInput, label, mode, setValue]);

  // Handles triggering the validation again after a successful form submission
  // in case the backend returns with a conflicting name.
  useEffect(() => {
    if (isSubmitSuccessful) {
      trigger('identifier', { shouldFocus: true });
    }
  }, [isSubmitSuccessful, trigger]);

  return (
    <HookFormText
      control={control}
      label="Technical identifier"
      name="identifier"
      disabled={isSubmitting}
      helpIcon={{
        text: 'Unique identifier for the custom attribute used to represent field data in the API and exports. It can not be changed later.',
      }}
      rules={
        mode === 'create'
          ? {
              validate: {
                conflictingIdentifier: (identifier) =>
                  (identifier && !conflictingIdentifiers.has(identifier)) ||
                  `Attribute identifier '${identifier}' already in use`,
              },
              pattern: {
                value: /^[a-z0-9_]{1,}$/,
                message: 'Must contain valid characters only (a-z, 0-9, _)',
              },
              required: 'The identifier is required',
              maxLength: {
                value: maxLength,
                message: `Must not exceed ${maxLength} characters`,
              },
            }
          : undefined
      }
      componentProps={{
        placeholder: 'order_number',
        readOnly: mode === 'edit',
        onInput: () => setHasUserInput(true),
      }}
    />
  );
};
